import * as React from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Chip,
  ListItemText,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import {Select as MuiSelect} from '@mui/material';
import {TextField as MuiTextField} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import FilterListIcon from '@mui/icons-material/FilterList';

const generateRandomHex = (length) => {
    const randomNumber = Math.random().toString(16);
    return randomNumber.substr(2, length);
};

const listLabel = (value, list) => {
    return list.filter((cur) => value === cur.value)[0]?.label || value;
};

const FormMultiSelectChipsFilter = ({
    name,
    label,
    list,
    required,
    rules = {},
    enableSelectAll,
    enableTextFilter = false,
    enableSelectFilter = false,
    selectFilterList = [],
    defaultTextFilter = '',
    defaultSelectFilter = '',
}) => {
    const [selectAll, setSelectAll] = useState(false);
    const [noOptionsSelected, setNoOptionsSelected] = useState(false);
    const { control, formState, setValue } = useFormContext();

    const errors = formState.errors;

    const requiredDefaultRule = required ? { required: { value: true, message: 'This field is required' } } : {};
    rules = { ...requiredDefaultRule, ...rules };

    const labelId = name + '-label';

    const id = React.useRef(generateRandomHex(8)).current;
    const selectFilterLabelId = id + '-select-filter-label';

    const [filterValue, setFilterValue] = React.useState(defaultTextFilter);
    const [selectFilterValue, setSelectFilterValue] = React.useState(defaultSelectFilter);
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const changeFilter = (e) => {
        setFilterValue(e.target.value);
    };

    const changeSelectFilter = (e) => {
        setSelectFilterValue(e.target.value);
    };

    const resetFilters = () => {
        setFilterValue('');
        setSelectFilterValue('');
    };

    const hasFilters = enableTextFilter || enableSelectFilter;

    const filteredList = React.useMemo(() => {
        let result = list;
        if (enableTextFilter && filterValue.trim()) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(filterValue.toLowerCase())
            );
        }
        if (enableSelectFilter && selectFilterValue) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(String(selectFilterValue).toLowerCase())
            );
        }
        return result;
    }, [list, enableTextFilter, filterValue, enableSelectFilter, selectFilterValue]);

    //console.log("filter - select", selectFilterList, selectFilterValue, defaultSelectFilter);
    //console.log("filter - list", filteredList);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Controller
                    render={({ field: { onChange, value, onBlur } }) => {
                        value = value || [];
                        return (
                            <FormControl fullWidth error={errors[name] ? true : false}>
                                <InputLabel id={labelId} required={required}>
                                    {label}
                                </InputLabel>
                                <Select
                                    multiple
                                    labelId={labelId}
                                    onBlur={onBlur}
                                    value={value}
                                    onChange={(e) => {
                                        setNoOptionsSelected(false);
                                        if (e.target.value.includes('All')) {
                                            if (!selectAll) {
                                                const allValues = filteredList.map((cur) => cur.value);
                                                onChange(allValues);
                                            } else {
                                                onChange([]);
                                            }
                                            setSelectAll(!selectAll);
                                        } else {
                                            if (enableSelectAll && e.target.value.length === filteredList.length) {
                                                setSelectAll(true);
                                            }
                                            onChange(e.target.value);
                                        }
                                    }}
                                    input={<OutlinedInput label={label} />}
                                    renderValue={(selected) => (
                                        <Stack gap={1} direction="row" flexWrap="wrap">
                                            {selected.map((cur) => (
                                                <Chip
                                                    key={cur}
                                                    label={listLabel(cur, list)}
                                                    onDelete={() => {
                                                        setNoOptionsSelected(false);
                                                        setValue(
                                                            name,
                                                            value.filter((item) => item !== cur)
                                                        );
                                                    }}
                                                    deleteIcon={
                                                        <CancelIcon
                                                            onMouseDown={(event) => event.stopPropagation()}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                >
                                    {enableSelectAll && (
                                        <MenuItem key="All" value="All" disabled={noOptionsSelected}>
                                            <ListItemText
                                                primary={value.length === filteredList.length ? 'Unselect All' : 'Select All'}
                                            />
                                        </MenuItem>
                                    )}
                                    {filteredList.map((cur) => (
                                        <MenuItem
                                            disabled={noOptionsSelected}
                                            key={cur.value}
                                            value={cur.value}
                                            sx={{ justifyContent: 'space-between' }}
                                        >
                                            {cur.label}
                                            {value.includes(cur.value) ? <CheckIcon color="info" /> : null}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors[name] && (
                                    <FormHelperText>
                                        <Typography component="span">{errors[name]?.message?.toString()}</Typography>
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    }}
                    name={name}
                    control={control}
                    rules={rules}
                />
                {hasFilters && (
                    <IconButton onClick={() => setFiltersOpen((prev) => !prev)} aria-label="Toggle filters">
                        <FilterListIcon />
                    </IconButton>
                )}
            </Stack>
            {hasFilters && (
                <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {enableTextFilter && (
                            <MuiTextField
                                label={label + ' Free Filter'}
                                value={filterValue}
                                onChange={changeFilter}
                                fullWidth
                                size="small"
                            />
                        )}
                        {enableSelectFilter && (
                            <FormControl fullWidth size="small">
                                <InputLabel id={selectFilterLabelId}>{label + ' Select Filter'}</InputLabel>
                                <MuiSelect
                                    labelId={selectFilterLabelId}
                                    label={label + ' Select Filter'}
                                    value={selectFilterValue}
                                    onChange={changeSelectFilter}
                                >
                                    <MenuItem value=""><em>All</em></MenuItem>
                                    {selectFilterList.map((cur) => (
                                        <MenuItem key={cur.value || cur.label} value={cur.value}>{cur.label}</MenuItem>
                                    ))}
                                </MuiSelect>
                            </FormControl>
                        )}
                        <Button variant="outlined" size="small" onClick={resetFilters} sx={{ whiteSpace: 'nowrap' }}>Reset</Button>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
};

export default FormMultiSelectChipsFilter;
